import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { canShowPlans, isNativeApp } from "@/lib/platform";
import { openPaywall } from "@/lib/iap";

/**
 * TrialDaysPill — passive "Pro trial · Xd left" indicator visible for the
 * whole 7-day comped Pro window (unlike TrialEndingBanner, which only fires
 * in the last 2 days with an urgent CTA).
 *
 * Renders inline in the vessel header. Tap → paywall (native) or /pricing
 * (web). Dismissible per session so captains who don't want the reminder
 * can hide it. Never shows for paid Pro users.
 */
const DISMISS_KEY = "bm_trial_pill_dismissed";

function daysLeft(iso) {
    const ms = new Date(iso).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
}

export default function TrialDaysPill() {
    const { user } = useAuth();
    const nav = useNavigate();
    const [dismissed, setDismissed] = useState(
        () => sessionStorage.getItem(DISMISS_KEY) === "1",
    );

    const days = useMemo(() => (user?.pro_until ? daysLeft(user.pro_until) : 0), [user?.pro_until]);

    // Only for free-plan users still inside their Pro trial window
    if (dismissed) return null;
    if (!canShowPlans()) return null;
    if (!user?.pro_until) return null;
    if (user.subscription_status !== "free" || user.plan_tier !== "pro") return null;
    if (days <= 0) return null;

    const label = days === 1 ? "Pro trial · last day" : `Pro trial · ${days} days left`;

    const goToPlans = () => {
        if (isNativeApp()) {
            openPaywall();
        } else {
            nav("/pricing");
        }
    };

    return (
        <span
            data-testid="trial-days-pill"
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "5px 5px 5px 11px",
                borderRadius: 999,
                background: "rgba(212, 181, 114, 0.10)",
                border: "1px solid rgba(212, 181, 114, 0.45)",
                color: "var(--bm-gold)",
                fontSize: 12,
                fontWeight: 500,
                letterSpacing: "0.02em",
                whiteSpace: "nowrap",
            }}
        >
            <Sparkles size={12} />
            <button
                data-testid="trial-days-pill-cta"
                onClick={goToPlans}
                style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    color: "var(--bm-gold)",
                    font: "inherit",
                }}
            >
                {label}
            </button>
            <button
                data-testid="trial-days-pill-dismiss"
                onClick={() => {
                    sessionStorage.setItem(DISMISS_KEY, "1");
                    setDismissed(true);
                }}
                aria-label="Hide until next session"
                style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "var(--bm-gold-dim, rgba(212,181,114,0.6))",
                    padding: 3,
                    display: "flex",
                    borderRadius: 999,
                }}
            >
                <X size={11} />
            </button>
        </span>
    );
}
